import { useEffect, useState } from 'react';
import {
  FolderOpen, Loader2, Download, Trash2, Pencil, Image as ImageIcon, Upload,
} from 'lucide-react';
import { dokumenteApi } from '@/services/api';
import { documentCategoryLabels, type BauvorhabenDocument, type BauvorhabenDokumente, type DocumentCategory, type Gewerk } from '@/types';
import { formatDateOnly } from '@/utils/progress';
import { documentMimeGroup, documentGroupIcon, formatFileSize, DOCUMENT_ACCEPT } from './documentDisplay';
// Generische Bild-Lightbox aus dem Rechnungen-Modul (url/fileName/onClose) —
// später ggf. nach components/common verschieben, für Phase 1 Cross-Import.
import { InvoiceImageLightbox } from '@/components/rechnungen/InvoiceImageLightbox';

/**
 * Dokumente-Karte im Bauvorhaben-Detail: feste Kategorien mit Counts
 * (klickbarer Filter), Upload-Leiste für ALLE Modul-Nutzer (Bauleiter
 * fotografieren Mängel), Bilder als Thumbnail-Galerie mit Lightbox,
 * übrige Kategorien als Zeilen-Liste mit Download/Umbenennen/Löschen
 * (Umbenennen/Löschen nur approver+ — canManage).
 * State (data) gehört der Detail-Layoutroute; diese Karte ist bis auf
 * lokale Filter-/Upload-Zustände zustandslos.
 */

interface BauvorhabenDokumenteCardProps {
  data: BauvorhabenDokumente | null;
  bauvorhabenId: number;
  /** Aktive Gewerke des Akkordeons (für die optionale Gewerk-Verknüpfung). */
  gewerke: Gewerk[];
  /** approver+ → Umbenennen/Kategorie/Löschen. */
  canManage: boolean;
  onChanged: () => void;
  onError: (message: string) => void;
}

const CATEGORY_ORDER = Object.keys(documentCategoryLabels) as DocumentCategory[];

export function BauvorhabenDokumenteCard({
  data, bauvorhabenId, gewerke, canManage, onChanged, onError,
}: BauvorhabenDokumenteCardProps) {
  const [activeCategory, setActiveCategory] = useState<DocumentCategory | null>(null);
  const [lightboxDoc, setLightboxDoc] = useState<{ id: number; name: string | null } | null>(null);

  if (!data) {
    return (
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <FolderOpen className="h-5 w-5 text-gray-400" />
          Dokumente
        </h2>
        <p className="text-sm text-gray-500">Dokumente konnten nicht geladen werden.</p>
      </div>
    );
  }

  const { documents, summary } = data;
  const visible = activeCategory
    ? documents.filter((d) => d.category === activeCategory)
    : documents;
  const images = visible.filter((d) => d.category === 'bilder');
  const rows = activeCategory === 'bilder' ? [] : visible.filter((d) => d.category !== 'bilder');

  return (
    <div className="card p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <FolderOpen className="h-5 w-5 text-gray-400" />
        Dokumente
        <span className="text-sm font-normal text-gray-500">({summary.total_count})</span>
      </h2>

      {/* Kategorie-Chips mit Counts (klickbarer Filter) */}
      <div className="mb-4 flex flex-wrap gap-1.5">
        <button
          type="button"
          onClick={() => setActiveCategory(null)}
          className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors ${
            activeCategory === null ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          Alle ({summary.total_count})
        </button>
        {CATEGORY_ORDER.map((cat) => (
          <button
            key={cat}
            type="button"
            onClick={() => setActiveCategory(activeCategory === cat ? null : cat)}
            className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors ${
              activeCategory === cat ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {documentCategoryLabels[cat]} ({summary.counts_by_category[cat] ?? 0})
          </button>
        ))}
      </div>

      <UploadStrip
        bauvorhabenId={bauvorhabenId}
        gewerke={gewerke}
        onChanged={onChanged}
        onError={onError}
      />

      {documents.length === 0 ? (
        <p className="text-sm text-gray-500">Noch keine Dokumente für dieses Bauvorhaben.</p>
      ) : (
        <>
          {/* Bilder: Thumbnail-Galerie */}
          {images.length > 0 && (
            <div className="mb-4">
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-400">Bilder</p>
              <div className="grid grid-cols-4 gap-2 sm:grid-cols-6 lg:grid-cols-8">
                {images.map((doc) => (
                  <DocumentThumbnail
                    key={doc.id}
                    document={doc}
                    onOpen={() => setLightboxDoc({ id: doc.id, name: doc.original_name })}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Übrige Kategorien: Zeilen-Liste */}
          {rows.length > 0 && (
            <ul className="divide-y divide-gray-100">
              {rows.map((doc) => (
                <DocumentRow
                  key={doc.id}
                  document={doc}
                  canManage={canManage}
                  onChanged={onChanged}
                  onError={onError}
                />
              ))}
            </ul>
          )}
          {visible.length === 0 && (
            <p className="text-sm text-gray-500">Keine Dokumente in dieser Kategorie.</p>
          )}
        </>
      )}

      {lightboxDoc && (
        <DocumentLightbox
          docId={lightboxDoc.id}
          fileName={lightboxDoc.name}
          onClose={() => setLightboxDoc(null)}
        />
      )}
    </div>
  );
}

/** Upload-Leiste: Kategorie + Titel (Charge) + [Datum bei Bilder] + Dateien — für alle Modul-Nutzer. */
function UploadStrip({
  bauvorhabenId, gewerke, onChanged, onError,
}: {
  bauvorhabenId: number;
  gewerke: Gewerk[];
  onChanged: () => void;
  onError: (message: string) => void;
}) {
  const [category, setCategory] = useState<DocumentCategory>('sonstiges');
  const [title, setTitle] = useState('');
  const [takenAt, setTakenAt] = useState('');
  const [gewerkId, setGewerkId] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  const handleUpload = (files: FileList | null) => {
    const list = Array.from(files ?? []);
    if (list.length === 0) return;
    setIsUploading(true);
    dokumenteApi
      .upload({
        bauvorhaben_id: bauvorhabenId,
        category,
        title: title.trim() || undefined,
        taken_at: category === 'bilder' && takenAt ? takenAt : undefined,
        gewerk_id: gewerkId ? Number(gewerkId) : undefined,
        files: list,
      })
      .then((response) => {
        const errors = response.data.data.errors;
        if (errors.length > 0) {
          onError(`Einige Dateien fehlgeschlagen: ${errors.map((e) => e.name).join(', ')}`);
        }
        // Charge-Formular leeren — Kategorie bleibt stehen (typische Serie).
        setTitle('');
        setTakenAt('');
        onChanged();
      })
      .catch((err) => onError((err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Fehler beim Hochladen'))
      .finally(() => setIsUploading(false));
  };

  return (
    <div className="mb-4 rounded-lg border border-gray-100 bg-gray-50/60 p-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value as DocumentCategory)}
          className="input h-9 w-full py-1 text-sm sm:w-44"
          aria-label="Kategorie"
        >
          {CATEGORY_ORDER.map((cat) => (
            <option key={cat} value={cat}>{documentCategoryLabels[cat]}</option>
          ))}
        </select>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Titel / Chargen-Bezeichnung …"
          maxLength={255}
          className="input h-9 w-full flex-1 py-1 text-sm"
        />
        {category === 'bilder' && (
          <input
            type="date"
            value={takenAt}
            onChange={(e) => setTakenAt(e.target.value)}
            className="input h-9 w-full py-1 text-sm sm:w-40"
            aria-label="Foto-Datum"
          />
        )}
        <select
          value={gewerkId}
          onChange={(e) => setGewerkId(e.target.value)}
          className="input h-9 w-full py-1 text-sm sm:w-40"
          aria-label="Gewerk (optional)"
        >
          <option value="">Gewerk (optional)</option>
          {gewerke.map((g) => (
            <option key={g.id} value={g.id}>{g.name}</option>
          ))}
        </select>
        <label className="inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">
          {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          Dateien wählen
          <input
            type="file"
            multiple
            accept={DOCUMENT_ACCEPT}
            className="hidden"
            onChange={(e) => {
              handleUpload(e.target.files);
              e.target.value = '';
            }}
          />
        </label>
      </div>
      <p className="mt-1.5 text-xs text-gray-500">
        Titel gilt für alle gewählten Dateien (Charge) · PDF, Bilder, CSV, Excel oder Word — maximal 10 MB je Datei.
      </p>
    </div>
  );
}

/** Bild-Thumbnail im Galerie-Tile: Blob-URL laden, strikt revoken (Cleanup). */
function DocumentThumbnail({
  document, onOpen,
}: {
  document: BauvorhabenDocument;
  onOpen: () => void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    setIsLoading(true);
    dokumenteApi
      .getFileBlob(document.id)
      .then((response) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(response.data);
        setUrl(objectUrl);
        setIsLoading(false);
      })
      .catch(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [document.id]);

  return (
    <button
      type="button"
      onClick={() => url && onOpen()}
      title={(document.title || document.original_name || '') + (document.taken_at ? ` (${formatDateOnly(document.taken_at)})` : '')}
      className="group relative aspect-square overflow-hidden rounded bg-gray-100"
    >
      {isLoading ? (
        <span className="flex h-full w-full items-center justify-center">
          <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
        </span>
      ) : url ? (
        <img src={url} alt={document.original_name ?? 'Bild'} className="h-full w-full object-cover transition-transform group-hover:scale-105" />
      ) : (
        <span className="flex h-full w-full items-center justify-center text-gray-400">
          <ImageIcon className="h-5 w-5" />
        </span>
      )}
      {document.taken_at && (
        <span className="absolute inset-x-0 bottom-0 truncate bg-gray-900/60 px-1 py-0.5 text-center text-[10px] text-white">
          {formatDateOnly(document.taken_at)}
        </span>
      )}
    </button>
  );
}

/** Lightbox öffnet den Blob erst auf Klick (nicht pro Tile on mount) —
 *  Download über die Lightbox-URL; Blob hier kurzlebig. */
function DocumentLightbox({
  docId, fileName, onClose,
}: {
  docId: number;
  fileName: string | null;
  onClose: () => void;
}) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    dokumenteApi
      .getFileBlob(docId)
      .then((response) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(response.data);
        setUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) onClose();
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [docId, onClose]);

  if (!url) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/80" onClick={onClose}>
        <Loader2 className="h-8 w-8 animate-spin text-white" />
      </div>
    );
  }
  // InvoiceImageLightbox übernimmt Esc/Backdrop/Download (Blob-URL gehört hier).
  return <InvoiceImageLightbox url={url} fileName={fileName} onClose={onClose} />;
}

/** Zeile einer Nicht-Bilder-Kategorie: Icon, Titel, Badge, Meta, Aktionen. */
function DocumentRow({
  document, canManage, onChanged, onError,
}: {
  document: BauvorhabenDocument;
  canManage: boolean;
  onChanged: () => void;
  onError: (message: string) => void;
}) {
  const group = documentMimeGroup(document.file_mime, document.original_name);
  const Icon = documentGroupIcon[group];
  const [isDownloading, setIsDownloading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(document.title ?? '');
  const [editCategory, setEditCategory] = useState<DocumentCategory>(document.category);

  const handleDownload = () => {
    setIsDownloading(true);
    dokumenteApi
      .getFileBlob(document.id)
      .then((response) => {
        const url = URL.createObjectURL(response.data);
        // Prop `document` schattiert window.document — deshalb window.document.
        const a = window.document.createElement('a');
        a.href = url;
        a.download = document.original_name || `dokument-${document.id}`;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch((err) => onError((err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Fehler beim Herunterladen'))
      .finally(() => setIsDownloading(false));
  };

  const handleDelete = () => {
    if (!window.confirm(`Dokument "${document.title ?? document.original_name ?? document.id}" löschen?`)) return;
    dokumenteApi
      .remove(document.id)
      .then(() => onChanged())
      .catch((err) => onError((err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Fehler beim Löschen'));
  };

  const handleSaveEdit = () => {
    dokumenteApi
      .update(document.id, {
        title: editTitle.trim() || null,
        category: editCategory,
      })
      .then(() => {
        setIsEditing(false);
        onChanged();
      })
      .catch((err) => onError((err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Fehler beim Speichern'));
  };

  return (
    <li className="py-2">
      {isEditing ? (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <input
            type="text"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            maxLength={255}
            placeholder="Titel …"
            className="input h-8 flex-1 py-1 text-sm"
          />
          <select
            value={editCategory}
            onChange={(e) => setEditCategory(e.target.value as DocumentCategory)}
            className="input h-8 py-1 text-sm sm:w-44"
          >
            {CATEGORY_ORDER.map((cat) => (
              <option key={cat} value={cat}>{documentCategoryLabels[cat]}</option>
            ))}
          </select>
          <div className="flex shrink-0 gap-2">
            <button type="button" onClick={handleSaveEdit} className="btn btn-secondary h-8 px-3 py-0 text-xs">Speichern</button>
            <button type="button" onClick={() => { setIsEditing(false); setEditTitle(document.title ?? ''); setEditCategory(document.category); }} className="btn btn-secondary h-8 px-3 py-0 text-xs">Abbrechen</button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded bg-gray-50 text-gray-500">
            <Icon className="h-4 w-4" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-gray-900" title={document.original_name ?? undefined}>
              {document.title || document.original_name || `Dokument #${document.id}`}
            </p>
            <p className="flex flex-wrap items-center gap-x-2 text-xs text-gray-500">
              <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600">
                {documentCategoryLabels[document.category]}
              </span>
              {formatFileSize(document.file_size) && <span>{formatFileSize(document.file_size)}</span>}
              {document.taken_at && <span>Foto vom {formatDateOnly(document.taken_at)}</span>}
              {document.gewerk_name && <span>Gewerk: {document.gewerk_name}</span>}
              {document.uploaded_by_name && <span>von {document.uploaded_by_name}</span>}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={handleDownload}
              disabled={isDownloading}
              title="Herunterladen"
              className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50"
            >
              {isDownloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            </button>
            {canManage && (
              <button
                type="button"
                onClick={() => setIsEditing(true)}
                title="Titel/Kategorie ändern"
                className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
              >
                <Pencil className="h-4 w-4" />
              </button>
            )}
            {canManage && (
              <button
                type="button"
                onClick={handleDelete}
                title="Dokument löschen"
                className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      )}
    </li>
  );
}